import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';

import { firebaseAuthClient } from '@/src/modules/auth/auth.config';
import { createPaymentIntent } from '@/src/modules/stripe/payment';
import { IFirestoreOffer } from '@/src/modules/firestore/offers/offers.interface';
import { IFirestorePromo } from '@/src/modules/firestore/promos/promos.interface';
import { useBraze } from '@/src/utils/braze/braze';
import { useGoogleTagManagerHandler } from '@/src/components/ui/GoogleTagManagerHandler/useGoogleTagManagerHandler';
import { IResolvedErrors } from '@/src/utils/collections/errors.mapper';
import { ICustomError } from '@/src/types/api';
import { formatDuration } from '@/src/utils/format/date';
import { formatVolume } from '@/src/utils/format/volume';
import { applyDiscount } from '@/src/utils/format/amount';

interface IProps {
  offers: IFirestoreOffer[];
  promos: Record<string, IFirestorePromo>;
  errorTexts?: IResolvedErrors | null;
}

export const useDestinationDetailsOffers = ({
  offers,
  promos,
  errorTexts,
}: IProps) => {
  const [selectedOffer, setSelectedOffer] = useState<IFirestoreOffer>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ICustomError>();
  const brazeInstance = useBraze();
  const { locale } = useParams();
  const router = useRouter();
  const { cookieAcceptList, handleEventViewItem } =
    useGoogleTagManagerHandler();

  useEffect(() => {
    if (selectedOffer) {
      handleEventViewItem(selectedOffer);
    }
  }, [selectedOffer]);

  useEffect(() => {
    setSelectedOffer(offers[0]);
  }, []);

  const callBraze = async () => {
    // @ts-ignore
    if (brazeInstance && cookieAcceptList?.braze === true && selectedOffer) {
      await brazeInstance.then(braze => {
        braze?.logCustomEvent('on_click_offer', {
          zone: selectedOffer.zone,
        });
      });
    }
  };

  useEffect(() => {
    window.dataLayer?.push({
      event: 'view_item_list',
      ecommerce: {
        currency: 'EUR',
        items: [
          offers.map((offer, index) => ({
            item_id: offer.id,
            item_name: `eSIM for ${offer.id}`,
            item_variant: formatVolume(offer.volume),
            item_category: formatDuration(offer.duration),
            item_category2: offer.zone,
            quantity: 1,
            discount:
              promos[offer.id] && promos[offer.id]?.type === 'flatDiscount'
                ? applyDiscount(offer, promos[offer.id]).amount / 100
                : 0,
            price: offer.basePriceEuros / 100,
            item_list_name: 'Destination Details',
            index,
          })),
        ],
      },
    });
  }, []);

  useEffect(() => {
    callBraze();
  }, [brazeInstance, cookieAcceptList]);

  const handleSelectOffer = (id: string) => {
    const offer = offers.find(item => item.id === id);
    if (offer) {
      setSelectedOffer(offer);
    }
  };

  const handleConfirmOffer = async () => {
    const user = firebaseAuthClient.currentUser;

    try {
      if (selectedOffer) {
        if (user) {
          setIsLoading(true);
          const paymentIntent = await createPaymentIntent(
            selectedOffer.id,
            promos[selectedOffer.id]?.id
          );

          if (paymentIntent) {
            router.push(`/${locale}/payment/` + paymentIntent.id);
          } else {
            throw new Error('Unexpected response null');
          }
        } else {
          localStorage.setItem(
            'selectedOffer',
            JSON.stringify({
              selectedOffer: selectedOffer.id,
              promoId: promos[selectedOffer.id]?.id,
              expirationDate: Date.now() + 3600000,
              basePriceEuro: selectedOffer.basePriceEuros,
              volume: selectedOffer.volume,
              discount:
                promos[selectedOffer.id]?.type != 'extraData'
                  ? promos[selectedOffer.id]?.value
                  : 0,
              duration: selectedOffer.duration,
            })
          );
          router.push(`/${locale}/auth/sign-in`);
        }
      }
    } catch (err) {
      console.error(err);
      setError({
        title: errorTexts?.globalTitle || 'An error occurred',
        message: errorTexts?.globalText || err.message,
      });
    }
  };

  return {
    selectedOffer,
    handleSelectOffer,
    handleConfirmOffer,
    isLoading,
    error,
    setError,
  };
};
